// customised-handout-store.js
import { makeObservable, observable, action, runInAction, toJS } from 'mobx';
import StoreUtils from './store-utils';
import UserApi from '../rest-api/user-api';

const makeDexieSafe = (value) => JSON.parse(JSON.stringify(toJS(value)));

class CustomisedHandoutStore {
    customisedHandoutData = {};
    db;
    idToken;
    isInitialized = false;

    constructor(userId, idToken) {
        this.db = StoreUtils.createDexieUserDB(userId, 'CustomisedHandoutStore');
        this.idToken = idToken;

        // Define schema for customised handouts
        this.db.version(1).stores({
            customisedHandouts:
                'customisedId, userId, baseContentId, baseContentIdUserIdCompositeKey, baseLastUpdated, locale, customisedStatus, lastUpdated, updatedBy, updatedById, notes, attachedWhiteboardId',
        });

        makeObservable(this, {
            customisedHandoutData: observable,
            setCustomisedHandoutData: action,
            addOrUpdateCustomisedHandout: action,
            isInitialized: observable,
        });
    }

    async initialize(userId) {
        console.warn('CustomisedHandoutStore initialize');
        const storedHandouts = await this.db.customisedHandouts.toArray();
        console.warn('Current DB Version:', this.db.verno);

        runInAction(() => {
            for (const handout of storedHandouts) {
                this.customisedHandoutData[handout.customisedId] = handout;
            }
        });

        try {
            // Attempt to fetch fresh from backend
            await this.fetchCustomisedHandoutsFromBackend(userId);
        } catch (err) {
            console.error('CustomisedHandoutStore initialization fetch error:', err);
        }

        runInAction(() => {
            this.isInitialized = true;
        });
    }

    async fetchCustomisedHandoutsFromBackend(userId) {
        try {
            const result = await UserApi.getCustomisedHandouts(this.idToken);
            console.warn('RESULT in fetchCustomisedHandoutsFromBackend: ', result);

            if (result && Array.isArray(result.customisedHandouts)) {
                await this.setCustomisedHandoutData(result.customisedHandouts);
            }
        } catch (error) {
            console.error('Error fetching customised handouts from backend:', error);
        }
    }

    async setCustomisedHandoutData(arrayOfHandouts) {
        if (!arrayOfHandouts || arrayOfHandouts.length === 0) return;

        const safeHandouts = arrayOfHandouts.map((handout) => makeDexieSafe(handout));

        try {
            await this.db.customisedHandouts.bulkPut(safeHandouts);
        } catch (error) {
            console.error('CustomisedHandoutStore Dexie bulkPut error:', error);
        }

        runInAction(() => {
            safeHandouts.forEach((handout) => {
                this.customisedHandoutData[handout.customisedId] = handout;
            });
        });
    }

    async addOrUpdateCustomisedHandout(handout) {
        if (!handout || !handout.customisedId) {
            console.error('Invalid handout data:', handout);
            return;
        }

        const plainHandout = makeDexieSafe(handout);

        try {
            await this.db.customisedHandouts.put(plainHandout);
        } catch (error) {
            console.error('CustomisedHandoutStore Dexie put error:', error);
        }

        // Update observable
        runInAction(() => {
            this.customisedHandoutData[plainHandout.customisedId] = plainHandout;
        });

        console.warn('CustomisedHandout added/updated:', plainHandout.customisedId);
    }

    async getCustomisedHandoutById(customisedId) {
        // First check in-memory
        if (this.customisedHandoutData[customisedId]) {
            return this.customisedHandoutData[customisedId];
        }

        // Then check IndexedDB
        return await this.db.customisedHandouts.get(customisedId);
    }

    async getCustomisedHandoutByBaseContentId(baseContentId, userId) {
        const compositeKey = `${baseContentId}#${userId}`;

        // Search in IndexedDB
        const handouts = await this.db.customisedHandouts
            .where('baseContentIdUserIdCompositeKey')
            .equals(compositeKey)
            .toArray();

        // Return the first active customised handout
        return handouts.find((h) => h.customisedStatus === 'active');
    }

    async deleteDatabase() {
        await this.db.delete();
    }
}

export default async function createCustomisedHandoutStore(userId, idToken) {
    const store = new CustomisedHandoutStore(userId, idToken);
    await store.initialize(userId);
    return store;
}
